import { useEffect, useRef } from 'react';
import api from '../utils/api';

/**
 * usePaginationGuard
 *
 * Handles two pagination-specific concerns for every collection page:
 *
 *  1. Empty-page redirect
 *     If the API returns zero products on page > 1 (e.g. due to a stale URL or
 *     the user navigating beyond what the server can serve), automatically jump
 *     back to the last valid page instead of showing a blank grid.
 *
 *  2. Adjacent-page prefetch
 *     After products load, quietly fire a background request for page N+1 to
 *     warm the server pipeline.  Failures are silently swallowed.
 *
 * @param {object}   opts
 * @param {any[]}    opts.products       Current page's product array
 * @param {number}   opts.totalProducts  Total navigable count returned by API
 * @param {number}   opts.currentPage    Currently displayed page (1-indexed)
 * @param {number}   opts.itemsPerPage   Items per page (constant per collection)
 * @param {Function} opts.setCurrentPage Setter to trigger a safe redirect
 * @param {Function} [opts.buildParams]  Optional fn(page) → axios params object
 *                                       required to enable prefetching
 */
function usePaginationGuard({
  products,
  totalProducts,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  buildParams,
}) {
  const prefetchTimerRef = useRef(null);
  const lastPrefetchedPage = useRef(null);
  const isPrefetching = useRef(false);

  // ── 1. Empty-page redirect ───────────────────────────────────────────────
  useEffect(() => {
    if (products.length === 0 && currentPage > 1 && totalProducts > 0) {
      const lastValidPage = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
      if (currentPage > lastValidPage) {
        setCurrentPage(lastValidPage);
      }
    }
  }, [products.length, currentPage, totalProducts, itemsPerPage, setCurrentPage]);

  // ── 2. Adjacent-page prefetch (N+1) ────────────────────────────────────
  useEffect(() => {
    if (!buildParams || products.length === 0) return;

    const totalPages = Math.ceil(totalProducts / itemsPerPage);
    const nextPage = currentPage + 1;

    if (nextPage > totalPages) return;
    if (lastPrefetchedPage.current === nextPage) return;

    clearTimeout(prefetchTimerRef.current);

    prefetchTimerRef.current = setTimeout(async () => {
      if (isPrefetching.current) return;
      isPrefetching.current = true;
      lastPrefetchedPage.current = nextPage;
      try {
        await api.get('/api/products', { params: buildParams(nextPage) });
      } catch (_) {
        // Prefetch failures must be silent
      } finally {
        isPrefetching.current = false;
      }
    }, 600);

    return () => clearTimeout(prefetchTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, products.length, totalProducts, itemsPerPage]);
}

export default usePaginationGuard;
